import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage } from './pages/Login/LoginPage';
import { PositionPage } from './pages/Position/PositionPage';
import { CreatePlanningPage } from './pages/CreatePlanning/CreatePlanningPage';
import { DetailPage } from './pages/Detail/DetailPage';
import { EditDetailPage } from './pages/EditDetail/EditDetailPage';
import { ViewDetailPage } from './pages/ViewDetail/ViewDetailPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { StatsPage } from './pages/Stats/StatsPage';
import { NotificationsPage } from './pages/Notifications/NotificationsPage';
import { InstallPrompt } from './components/InstallPrompt/InstallPrompt';
import { PushPermissionPrompt } from './components/PushPermissionPrompt/PushPermissionPrompt';
import { UpdateController } from './components/UpdateController/UpdateController';
import { LogoutOverlay } from './components/LogoutOverlay/LogoutOverlay';
import { DiscardedPhotoNotice } from './components/DiscardedPhotoNotice/DiscardedPhotoNotice';
import { useSyncCoordinator } from './hooks/useOfflineSync';
import { useDeliveryNoteSync } from './hooks/useDeliveryNoteSync';
import { useFeatures } from './hooks/useFeatures';
import type { FeatureFlags } from './services/features';
import { track } from './services/analytics';
import { startNotificationsPolling, stopNotificationsPolling } from './services/notifications';
import { resubscribeIfPermissionGranted } from './services/pushNotifications';

// Retardo de lo secundario al arrancar (avisos, suscripción push).
const STARTUP_DEFER_MS = 3000;

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Ruta que, además de sesión, exige una capacidad del perfil (services/features.ts).
// Sin ella devolvemos al listado en lugar de servir una pantalla que no le
// corresponde al usuario: ocultar el botón de entrada no basta si la URL es
// accesible a mano. Reutilizable para futuras pantallas gated.
function FeatureRoute({ feature, children }: { feature: keyof FeatureFlags; children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const features = useFeatures();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return features[feature] ? <>{children}</> : <Navigate to="/positions" replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  // Coordinador global de la cola offline: dispara reintentos al volver la
  // red, escucha mensajes del SW y mantiene el contador actualizado.
  useSyncCoordinator();
  // Cola aparte del documento de transporte (DDT) del proveedor.
  useDeliveryNoteSync();
  // Polling de avisos para alimentar el badge del BottomNav. Solo arranca
  // tras login (sin token la API responde 401). Al hacer logout paramos.
  // Además, re-subscribimos a push silenciosamente si el navegador ya tenía
  // el permiso concedido — así el user no tiene que volver a tocar
  // "Activar notificaciones" tras cada login.
  //
  // Arrancan STARTUP_DEFER_MS después (sep 2026): al abrir la app compiten con
  // la lista de cargas por las 6 conexiones del navegador, y nada de esto corre
  // prisa.
  useEffect(() => {
    if (!isAuthenticated) {
      stopNotificationsPolling();
      return;
    }
    const timer = setTimeout(() => {
      startNotificationsPolling();
      void resubscribeIfPermissionGranted();
    }, STARTUP_DEFER_MS);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/positions" replace /> : <LoginPage />} />
      <Route path="/positions" element={<PrivateRoute><PositionPage /></PrivateRoute>} />
      <Route path="/create-planning" element={<FeatureRoute feature="createPlanning"><CreatePlanningPage /></FeatureRoute>} />
      <Route path="/detail/:idCargo" element={<PrivateRoute><DetailPage /></PrivateRoute>} />
      <Route path="/edit-detail/:idCargo/:idDetail" element={<PrivateRoute><EditDetailPage /></PrivateRoute>} />
      <Route path="/view-detail/:idDetail" element={<PrivateRoute><ViewDetailPage /></PrivateRoute>} />
      <Route path="/stats" element={<PrivateRoute><StatsPage /></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="*" element={<Navigate to={isAuthenticated ? '/positions' : '/login'} replace />} />
    </Routes>
  );
}

// Banner antiguo. Lo conservamos definido pero ya no se monta — toda la
// lógica de actualización pasó a UpdateController + updateManager, que
// aplica silenciosamente cuando es seguro y queda en espera cuando hay
// edición en curso o cola offline. Si en el futuro se quiere revertir,
// basta con volver a renderizar <UpdateBanner /> en lugar de <UpdateController />.
// @ts-expect-error reservada como fallback histórico, no se monta.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function UpdateBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const regRef = useRef<ServiceWorkerRegistration | null>(null);
  // Flag persistente para no spamear el evento `app_update_prompt_shown`
  // en cada tick del polling. Sin esto el event se dispararía cada 30s
  // mientras el usuario tiene el banner visible.
  const promptTrackedRef = useRef(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => { regRef.current = reg; });
    }

    const currentBuildTime = __BUILD_TIME__;
    const checkVersion = async () => {
      try {
        const r = await fetch('/build-info.json?t=' + Date.now(), { cache: 'no-store' });
        if (!r.ok) return;
        const data = await r.json() as { buildTime: number };
        if (data.buildTime !== currentBuildTime) {
          if (!promptTrackedRef.current) {
            promptTrackedRef.current = true;
            track({ name: 'app_update_prompt_shown' });
          }
          setShowBanner(true);
          regRef.current?.update();
        }
      } catch { /* sin conexión */ }
    };

    const id = setInterval(checkVersion, 30_000);
    return () => clearInterval(id);
  }, []);

  const applyUpdate = () => {
    track({ name: 'app_update_applied' });
    const reg = regRef.current;
    if (reg?.waiting) {
      // Registrar el listener ANTES de enviar SKIP_WAITING para no perdernos
      // el evento si el SW activa rápido. Con un timeout de seguridad por
      // si el evento no llegara, recargamos igual a los 1.5s.
      let reloaded = false;
      const reload = () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });
      setTimeout(reload, 1500);
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      window.location.reload();
    }
  };

  if (!showBanner) return null;
  return (
    <div
      onClick={applyUpdate}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'var(--update-toast-bg)', color: 'var(--text-strong)',
        padding: '12px 16px', textAlign: 'center',
        fontSize: '14px', fontWeight: 700, cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}
    >
      ↻ Nueva versión disponible — toca para actualizar
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <UpdateController />
        <AppRoutes />
        <InstallPrompt />
        <PushPermissionPrompt />
        <DiscardedPhotoNotice />
        <LogoutOverlay />
      </BrowserRouter>
    </AuthProvider>
  );
}
